import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import useSeo from "../../hooks/useSeo";
import "./Public.css";

const pubUrl = process.env.PUBLIC_URL || "";
const SHINGLE_TYPES = [
  {
    id: "architectural",
    label: "Architectural Shingle",
    blurb: "Balanced curb appeal with the most common residential replacement profile.",
    pricePerSquare: 525,
    colors: [
      { id: "weathered-wood", label: "Weathered Wood" },
      { id: "charcoal", label: "Charcoal" },
      { id: "slate", label: "Slate" },
      { id: "cedar-brown", label: "Cedar Brown" },
    ],
  },
  {
    id: "designer",
    label: "Designer Shingle",
    blurb: "Heavier profile with more texture and a stronger dimensional look from the street.",
    pricePerSquare: 645,
    colors: [
      { id: "driftwood", label: "Driftwood" },
      { id: "midnight", label: "Midnight Black" },
      { id: "granite", label: "Granite Blend" },
      { id: "autumn", label: "Autumn Blend" },
    ],
  },
  {
    id: "impact",
    label: "Impact-Resistant Shingle",
    blurb: "Higher-spec laminate option for homeowners prioritizing resilience and warranty coverage.",
    pricePerSquare: 735,
    colors: [
      { id: "storm-gray", label: "Storm Gray" },
      { id: "walnut", label: "Walnut" },
      { id: "graphite", label: "Graphite" },
      { id: "weathered-oak", label: "Weathered Oak" },
    ],
  },
];

function getHouseConfig(squareFootage) {
  if (squareFootage < 2000) {
    return {
      label: "Small home",
      variant: "small",
      image: `${pubUrl}/images/visualizer/small.png`,
    };
  }

  if (squareFootage < 3200) {
    return {
      label: "Medium home",
      variant: "medium",
      image: `${pubUrl}/images/visualizer/medium.png`,
    };
  }

  return {
    label: "Large home",
    variant: "large",
    image: `${pubUrl}/images/visualizer/large.png`,
  };
}

function getRoofFactor(sliderValue) {
  const rise = getRoofRise(sliderValue);
  const run = 82;
  return Math.sqrt(1 + (rise / run) ** 2);
}

function getRoofRise(sliderValue) {
  return Math.min(90, Math.max(0, sliderValue * 0.75));
}

function getRoofDescriptor(sliderValue) {
  if (sliderValue < 25) return "Lower slope";
  if (sliderValue < 50) return "Balanced slope";
  if (sliderValue < 75) return "Steeper slope";
  return "High slope";
}

export default function ResidentialRoofVisualizer() {
  useSeo({
    title: "Roof Visualizer & Estimator | Crowned Roofing",
    description:
      "Preview roof shape, home size, and rough replacement sizing with Crowned Roofing's residential roof visualizer.",
    ogImage: "https://www.crownedroofing.com/images/ogimg.png",
    canonical: "https://www.crownedroofing.com/residential/roof-visualizer",
  });

  const navigate = useNavigate();
  const [squareFootage, setSquareFootage] = useState(2200);
  const [roofShapeValue, setRoofShapeValue] = useState(42);
  const [selectedTypeId, setSelectedTypeId] = useState("architectural");
  const [selectedColorId, setSelectedColorId] = useState("weathered-wood");

  const selectedType = SHINGLE_TYPES.find((type) => type.id === selectedTypeId) || SHINGLE_TYPES[0];
  const selectedColor =
    selectedType.colors.find((color) => color.id === selectedColorId) || selectedType.colors[0];
  const house = getHouseConfig(squareFootage);
  const roofFactor = getRoofFactor(roofShapeValue);
  const roofRise = getRoofRise(roofShapeValue);
  const roofPeakY = 102 - roofRise;

  const estimate = useMemo(() => {
    const footprint = Math.max(squareFootage, 800);
    const roofArea = Math.round(footprint * roofFactor);
    const wasteFactor = 1.1;
    const materialSquares = Math.ceil((roofArea * wasteFactor) / 100);
    const lowEstimate = materialSquares * selectedType.pricePerSquare;
    const highEstimate = Math.round(lowEstimate * 1.18);

    return {
      footprint,
      roofArea,
      materialSquares,
      lowEstimate,
      highEstimate,
    };
  }, [roofFactor, selectedType.pricePerSquare, squareFootage]);

  const handleTypeChange = (typeId) => {
    const nextType = SHINGLE_TYPES.find((type) => type.id === typeId);
    setSelectedTypeId(typeId);
    if (nextType && !nextType.colors.some((color) => color.id === selectedColorId)) {
      setSelectedColorId(nextType.colors[0].id);
    }
  };

  return (
    <main className="publicPage">
      <Header />

      <section className="rvHero">
        <div className="container rvHeroInner">
          <div className="rvHeroCopy">
            <span className="rvEyebrow">Residential roof planner</span>
            <h1>Match the home size, then drag the roofline until it looks right.</h1>
            <p>
              This keeps the roof selector visual. Pick the house size by square footage, use the
              slider to shape the roof angle, and save the material/color decision for later.
            </p>
            <div className="rvHeroActions">
              <button className="rBtn" onClick={() => navigate("/contact")}>
                Request a Real Estimate
              </button>
              <button className="rvGhostBtn" onClick={() => navigate("/residential")}>
                Back to Residential Roofing
              </button>
            </div>
          </div>

          <div className="rvSnapshotCard">
            <div className="rvSnapshotLabel">Current selection</div>
            <div className="rvSnapshotValue">{house.label}</div>
            <div className="rvSnapshotMeta">
              {squareFootage.toLocaleString()} sq ft | {getRoofDescriptor(roofShapeValue)} | {selectedType.label}
            </div>
            <div className="rvSnapshotRange">
              ${estimate.lowEstimate.toLocaleString()} to ${estimate.highEstimate.toLocaleString()}
            </div>
            <div className="rvSnapshotNote">
              Planning range only. Real pricing still depends on measurements, access, tear-off,
              flashing, ventilation, and deck condition.
            </div>
          </div>
        </div>
      </section>

      <section className="rvPlannerSection">
        <div className="container rvPlannerGrid">
          <div className="rvControls">
            <div className="rvPanel">
              <div className="rvPanelHead">
                <h2>Build the roof</h2>
                <p>Use square footage for the house image and a continuous slider for the roof angle.</p>
              </div>

              <label className="rvField">
                <span>Home square footage</span>
                <div className="rvFieldRow">
                  <input
                    className="rvNumberInput"
                    type="number"
                    min="800"
                    max="5000"
                    step="50"
                    value={squareFootage}
                    onChange={(event) =>
                      setSquareFootage(
                        Math.min(5000, Math.max(800, Number(event.target.value) || 800))
                      )
                    }
                  />
                  <span className="rvFieldHint">
                    Small: 800-1999 | Medium: 2000-3199 | Large: 3200+
                  </span>
                </div>
                <input
                  className="rvSlider"
                  type="range"
                  min="800"
                  max="5000"
                  step="50"
                  value={squareFootage}
                  onChange={(event) => setSquareFootage(Number(event.target.value))}
                />
              </label>

              <label className="rvField">
                <span>Roof angle</span>
                <div className="rvFieldRow">
                  <strong>{getRoofDescriptor(roofShapeValue)}</strong>
                  <span className="rvFieldHint">Drag until the side view matches the roof shape you have in mind.</span>
                </div>
                <div className="rvPitchVisualizer" aria-hidden="true">
                  <svg viewBox="0 0 320 140" role="presentation">
                    <line x1="56" y1="102" x2="264" y2="102" stroke="rgba(11,19,36,0.14)" strokeWidth="6" />
                    <path
                      d={`M78 102 L160 ${roofPeakY} L242 102`}
                      fill="none"
                      stroke="#0b1324"
                      strokeWidth="10"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                    <circle cx="160" cy={roofPeakY} r="7" fill="var(--accent)" />
                  </svg>
                </div>
                <input
                  className="rvSlider"
                  type="range"
                  min="0"
                  max="120"
                  step="1"
                  value={roofShapeValue}
                  onChange={(event) => setRoofShapeValue(Number(event.target.value))}
                  aria-label="Roof angle"
                />
                <div className="rvPitchMarks rvPitchWords">
                  <span>Lower</span>
                  <span>Balanced</span>
                  <span>Steeper</span>
                </div>
              </label>
            </div>

            <div className="rvPanel">
              <div className="rvPanelHead">
                <h2>Pick the shingle</h2>
                <p>The material choices are here for planning, but the house image stays fixed for size only.</p>
              </div>

              <div className="rvTypeGrid">
                {SHINGLE_TYPES.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    className={`rvTypeCard ${selectedType.id === type.id ? "isActive" : ""}`}
                    onClick={() => handleTypeChange(type.id)}
                  >
                    <span className="rvTypeTitle">{type.label}</span>
                    <span className="rvTypeBlurb">{type.blurb}</span>
                    <span className="rvTypePrice">From ${type.pricePerSquare}/square</span>
                  </button>
                ))}
              </div>

              <label className="rvField rvSelectField">
                <span>Shingle color</span>
                <select
                  className="rvSelect"
                  value={selectedColor.id}
                  onChange={(event) => setSelectedColorId(event.target.value)}
                >
                  {selectedType.colors.map((color) => (
                    <option key={color.id} value={color.id}>
                      {color.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div className="rvVisualizerCol">
            <div className="rvPanel rvVisualizerPanel">
              <div className="rvPanelHead">
                <h2>House preview</h2>
                <p>The image changes only when the square footage crosses your small, medium, and large thresholds.</p>
              </div>

              <div className="rvScene">
                <div className="rvSceneBadge">{house.label}</div>
                <img className={`rvHouseImage rvHouseImage--${house.variant}`} src={house.image} alt={house.label} />
              </div>
            </div>

            <div className="rvPanel rvEstimatePanel">
              <div className="rvPanelHead">
                <h2>Planning numbers</h2>
                <p>Useful for rough budgeting before a field measurement and formal scope review.</p>
              </div>

              <div className="rvEstimateGrid">
                <div className="rvEstimateStat">
                  <span>Estimated roof footprint</span>
                  <strong>{estimate.footprint.toLocaleString()} sq ft</strong>
                </div>
                <div className="rvEstimateStat">
                  <span>Adjusted roof area</span>
                  <strong>{estimate.roofArea.toLocaleString()} sq ft</strong>
                </div>
                <div className="rvEstimateStat">
                  <span>Material quantity</span>
                  <strong>{estimate.materialSquares} squares</strong>
                </div>
                <div className="rvEstimateStat isAccent">
                  <span>Rough installed range</span>
                  <strong>
                    ${estimate.lowEstimate.toLocaleString()} to ${estimate.highEstimate.toLocaleString()}
                  </strong>
                </div>
              </div>

              <div className="rvEstimateNote">
                Pricing shifts with tear-off count, ventilation upgrades, decking repairs, valleys,
                flashing details, access, and disposal. Use this as a planning tool only.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="rFinal">
        <div className="rFinalCard">
          <div className="rFinalTitle">Turn the preview into a real scope</div>
          <div className="rFinalSub">
            Once the general size and roof shape feel right, we can inspect the house and turn it
            into a proper replacement proposal.
          </div>
          <button className="rBtn" onClick={() => navigate("/contact")}>
            Book an On-Site Estimate
          </button>
        </div>
      </section>

      <Footer />
    </main>
  );
}
